import React, { ReactElement, useEffect, useState } from 'react'
import dayjs from 'dayjs'

import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchCredentials,
  fetchUnstartedComplianceCredentialItems,
  selectCredentialsV2,
  selectCredentialsStatus,
  fetchLinkedServices,
  selectLinkedServices,
  selectLinkedServicesStatus,
  selectCredentialVerifications,
  selectCredentialVerificationsStatus,
  fetchCredentialVerifications,
  selectUnstartedComplianceCredentialItems,
  selectUnstartedComplianceCredentialItemsStatus,
} from 'src/v3/profile/profile.slice'
import {
  selectLinkedServices as selectLinkedServiceIDs,
  selectUseCompliance,
} from 'src/v3/settings/settings.slice'

import {
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  PreviewIcon,
} from 'src/v3/core/icons'
import AddIcon from '@mui/icons-material/Add'
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom'
import HourglassTopIcon from '@mui/icons-material/HourglassTop'

import {
  CredentialV2,
  CREDENTIAL_TYPES,
  VerificationStatus,
} from 'src/v3/core/types'
import axios, { pluckData, pluckError } from 'src/utilities/http-client'
import {
  ContentType,
  downloadFile,
  extensions,
} from 'src/v3/utilities/filesystem'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import ConfirmAction from 'src/v3/components/confirm-action'
import BasePage from '../layout'
import { CredentialForm } from './credential-form'
import { CredentialPreviewDialog } from './credential-preview-dialog'
import { VerificationStatusInfo } from '../verification-status'

type ExpirationStatus = 'Active' | 'Expiring Soon' | 'Expired'

function expirationStatus(expiration: string): ExpirationStatus {
  let date = dayjs(expiration)
  let daysToExpiration = date.diff(dayjs(), 'day')
  return daysToExpiration < 0
    ? 'Expired'
    : daysToExpiration > 30
      ? 'Active'
      : 'Expiring Soon'
}

function getCredential(id: string) {
  return axios
    .get<string>(`/api/v2/profile/credential/image-url?CredentialID=${id}`)
    .then(pluckData)
}

const createCredential = 'create'

let CredentialCard = ({
  credential,
  verificationEnabled,
  verificationStatuses,
  deleting,
  onEdit,
  onDelete,
  onError,
}: {
  credential: CredentialV2
  verificationEnabled: boolean
  verificationStatuses?: Record<string, VerificationStatus>
  deleting: boolean
  onEdit: () => void
  onDelete: () => void
  onError: (error: string) => void
}): ReactElement => {
  let status = expirationStatus(credential.expiration)
  let [downloading, setDownloading] = useState<'preview' | 'download'>()
  let [viewBlob, setViewBlob] = useState<Blob | null>(null)

  let ExpirationIcon =
    status === 'Active' ? HourglassTopIcon : HourglassBottomIcon

  return (
    <Card
      sx={{
        px: 2,
        py: 1,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
      }}
    >
      <Tooltip
        title={credential.type.kind === 'Other' ? credential.type.value : ''}
      >
        <Chip
          label={credential.type.kind}
          tabIndex={credential.type.kind === 'Other' ? 0 : undefined}
          sx={{
            borderRadius: '6px',
            flexBasis: 80,
            cursor: credential.type.kind === 'Other' ? 'help' : undefined,
          }}
        />
      </Tooltip>
      <Box sx={{ flexGrow: 1 }}>
        <Typography sx={{ pl: 0.5 }}>
          {credential.credentialIDNumber}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 1.5,
            fontSize: 14,
          }}
        >
          {verificationEnabled && (
            <Box>
              {verificationStatuses && (
                <VerificationStatusInfo
                  name="Credential"
                  verifications={verificationStatuses}
                  showText
                />
              )}
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: 0.5,
            }}
          >
            <Box>
              <ExpirationIcon sx={{ height: 18, width: 18, mb: '2px' }} />
              <Box component="span" sx={{ color: 'text.secondary' }}>
                {dayjs(credential.expiration).format('MM/DD/YYYY')}
              </Box>
            </Box>
            <Typography>{'\u2022'}</Typography>
            <Typography
              color="text.secondary"
              sx={{
                fontSize: 'inherit',
              }}
            >
              {expirationStatus(credential?.expiration)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{ display: 'flex', gap: 0.25 }}
        role="group"
        aria-label={`${credential.type.kind} credential actions`}
      >
        <Tooltip title="View">
          <span>
            <IconButton
              size="small"
              aria-label={`Preview ${credential.type.kind} credential`}
              loading={downloading === 'preview'}
              onClick={() => {
                setDownloading('preview')
                getCredential(credential.id)
                  .then((data) =>
                    axios
                      .get<Blob>(data, { responseType: 'blob' })
                      .then(pluckData),
                  )
                  .then((data) => setViewBlob(data))
                  .catch((err) =>
                    onError(
                      err?.response?.data ||
                        err?.message ||
                        'Error Getting Credential',
                    ),
                  )
                  .finally(() => setDownloading(undefined))
              }}
              disabled={!credential.imageName}
            >
              <PreviewIcon />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton
          size="small"
          disabled={!credential.imageName}
          loading={downloading === 'download'}
          aria-label={`Download ${credential.type.kind} credential`}
          onClick={() => {
            setDownloading('download')
            getCredential(credential.id)
              .then((response) =>
                axios
                  .get<Blob>(response, { responseType: 'blob' })
                  .catch(() => {
                    // eslint-disable-next-line fp/no-throw, no-throw-literal
                    throw 'File Not Found'
                  }),
              )
              .then(pluckData)
              .then((response) =>
                downloadFile(
                  response,
                  `${
                    credential.type.kind || credential.id
                  }-credential.${extensions[response.type as ContentType]}`,
                ),
              )
              .catch((err) => {
                onError(`Image Download Failed: ${err.response?.data || err}`)
              })
              .finally(() => setDownloading(undefined))
          }}
        >
          <DownloadIcon aria-hidden="true" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onEdit}
          aria-label={`Edit ${credential.type.kind} credential`}
        >
          <EditIcon aria-hidden="true" />
        </IconButton>
        <IconButton
          size="small"
          onClick={onDelete}
          loading={deleting}
          aria-label={`Delete ${credential.type.kind} credential`}
        >
          <DeleteIcon aria-hidden="true" />
        </IconButton>
      </Box>
      <CredentialPreviewDialog
        blob={viewBlob}
        fileName={credential.imageName}
        onClose={() => setViewBlob(null)}
      />
    </Card>
  )
}

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let linkedServiceIDs = useAppSelector(selectLinkedServiceIDs)
  let linkedServices = useAppSelector(selectLinkedServices)
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let credentials = useAppSelector(selectCredentialsV2)
  let credentialsStatus = useAppSelector(selectCredentialsStatus)
  let verifications = useAppSelector(selectCredentialVerifications)
  let verificationsStatus = useAppSelector(selectCredentialVerificationsStatus)

  let useCompliance = useAppSelector(selectUseCompliance)
  let unstartedComplianceCredentialItems = useAppSelector(
    selectUnstartedComplianceCredentialItems,
  )
  let unstartedComplianceCredentialItemsStatus = useAppSelector(
    selectUnstartedComplianceCredentialItemsStatus,
  )

  let [credentialOpen, setCredentialOpen] = useState<string>()
  let [credentialPreFill, setCredentialPreFill] = useState<string>()
  let [deleteCredential, setDeleteCredential] = useState<string>('')
  let [deleting, setDeleting] = useState<string>()
  let [errorMessage, setErrorMessage] = useState<string>()

  let activeCredentials = credentials.filter(
    (c) =>
      expirationStatus(c.expiration) !== 'Expired' &&
      (!verifications ||
        Object.values(verifications[c.id] || {}).some(
          (st) => st === 'accepted',
        )),
  )
  let inactiveCredentials = credentials.filter(
    (c) =>
      expirationStatus(c.expiration) === 'Expired' ||
      (!!verifications &&
        !Object.values(verifications[c.id] || {}).some(
          (st) => st === 'accepted',
        )),
  )

  let editCredential =
    credentialOpen === createCredential
      ? undefined
      : credentials.find((c) => c.id === credentialOpen)

  let verificationEnabled =
    linkedServiceIDs.some(
      (lsid) =>
        linkedServices.find((ls) => ls.id === lsid)?.credentialVerification ===
        'full',
    ) || false

  useAutoFetch(
    useCompliance ? unstartedComplianceCredentialItemsStatus : 'fulfilled',
    fetchUnstartedComplianceCredentialItems,
  )

  useEffect(() => {
    if (credentialsStatus === 'uninitialized') {
      dispatch(fetchCredentials())
    }
  }, [credentialsStatus])

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (
      linkedServicesStatus === 'fulfilled' &&
      verificationEnabled &&
      verificationsStatus === 'uninitialized'
    ) {
      dispatch(fetchCredentialVerifications())
    }
  }, [verificationsStatus, linkedServicesStatus, linkedServices])

  let renderCredentialCard = (credential: CredentialV2) => (
    <CredentialCard
      key={credential.id}
      credential={credential}
      verificationEnabled={verificationEnabled}
      verificationStatuses={
        verifications && verificationEnabled
          ? verifications[credential.id] || {}
          : undefined
      }
      deleting={deleting === credential.id}
      onEdit={() => setCredentialOpen(credential.id)}
      onDelete={() => setDeleteCredential(credential.id)}
      onError={setErrorMessage}
    />
  )

  return (
    <BasePage>
      <Box>
        <Typography variant="h4" component="h2">
          Credentials
        </Typography>
        <Box>
          {useCompliance && unstartedComplianceCredentialItems.length > 0 && (
            <Box sx={{ mt: 1, mb: 2 }}>
              {unstartedComplianceCredentialItems.map((item) => (
                <Alert
                  key={item.id}
                  severity="warning"
                  sx={{ mb: 1 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => {
                        setCredentialPreFill(item.type)
                        setCredentialOpen(createCredential)
                      }}
                    >
                      Add Credential
                    </Button>
                  }
                >
                  Your manager requires a credential:{' '}
                  <strong>
                    {CREDENTIAL_TYPES.some(
                      (t) => t.toLowerCase() === item.type.toLowerCase(),
                    )
                      ? item.type.toUpperCase()
                      : item.type}
                  </strong>
                </Alert>
              ))}
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              gap: 2,
              mb: 1,
            }}
          >
            <Typography color="text.secondary" variant="h6">
              All Credentials
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCredentialOpen(createCredential)}
            >
              Add Credential
            </Button>
          </Box>
          <Card
            variant="outlined"
            sx={{ mb: 2, p: 3, bgcolor: 'background.paperDark' }}
          >
            <Typography variant="h6">Active Credentials</Typography>
            <Divider sx={{ mb: 2, mt: 1 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {activeCredentials.length ? (
                activeCredentials.map((credential) =>
                  renderCredentialCard(credential),
                )
              ) : (
                <Typography>
                  You do not have any active credentials on file.
                </Typography>
              )}
            </Box>
          </Card>
          <Card
            variant="outlined"
            sx={{ mb: 2, p: 3, bgcolor: 'background.paperDark' }}
          >
            <Typography variant="h6">Inactive Credentials</Typography>
            <Divider sx={{ mb: 2, mt: 1 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {inactiveCredentials.length ? (
                inactiveCredentials.map((credential) =>
                  renderCredentialCard(credential),
                )
              ) : (
                <Typography>
                  You do not have any inactive credentials on file.
                </Typography>
              )}
            </Box>
          </Card>
          <ConfirmAction
            open={deleteCredential}
            setOpen={setDeleteCredential}
            openText={deleteCredential || 'closed'}
            text="Are you sure you want to delete this credential?"
            executeAction={() => {
              setDeleting(deleteCredential)
              return axios
                .delete(`/api/profile/credential/${deleteCredential}`)
                .catch((err) =>
                  setErrorMessage(pluckError(err, 'Error Deleting Credential')),
                )
                .then(() => dispatch(fetchCredentials()))
                .then(() => {
                  if (useCompliance)
                    dispatch(fetchUnstartedComplianceCredentialItems())
                })
                .finally(() => setDeleting(undefined))
            }}
          />
          <CredentialForm
            key={`${credentialOpen}-${credentialPreFill}`}
            credential={editCredential}
            initialType={credentialPreFill}
            open={!!credentialOpen}
            setOpen={() => {
              setCredentialOpen(undefined)
              setCredentialPreFill(undefined)
            }}
            onError={setErrorMessage}
          />
        </Box>
        <Snackbar
          open={!!errorMessage}
          onClose={() => setErrorMessage('')}
          aria-live="polite"
          aria-atomic="true"
        >
          <Alert
            variant="filled"
            onClose={() => setErrorMessage('')}
            severity="error"
            role="alert"
            aria-live="assertive"
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </Box>
    </BasePage>
  )
}
